import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { fetchCmsCollection } from '../cms';
import LightboxModal from '../components/LightboxModal';

export default function ProjectDetailPage() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeImageIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    setLoading(true);
    fetchCmsCollection('projects').then(items => {
      const found = items.find(p => p.project_slug === slug);
      setProject(found || null);
      setLoading(false);
    });
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-[60vh] bg-[#11161a] text-white flex items-center justify-center">
        <p className="text-slate-400 font-mono text-sm animate-pulse">Henter projekt...</p>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-[60vh] bg-[#11161a] text-white py-20 px-4 text-center">
        <h1 className="text-3xl font-bold">Projektet blev ikke fundet</h1>
        <p className="mt-2 text-slate-400">Det søgte projekt findes ikke eller er udgået.</p>
        <Link to="/projekter" className="mt-6 inline-block rounded-full bg-[#d92323] px-6 py-3 font-bold text-white">
          Se alle projekter
        </Link>
      </div>
    );
  }

  const galleryList = project.gallery && project.gallery.length > 0 
    ? project.gallery 
    : (project.image || []);

  return (
    <div className="bg-white">
      <Helmet>
        <title>{project.title} · Referencer - T.H Byg</title>
        <meta name="description" content={project.summary || `Se projektet ${project.title} udført af T.H Byg v/ Jacob Hartvig Hansen.`} />
      </Helmet>

      {/* Hero */}
      <section className="relative bg-[#11161a] text-white py-16 sm:py-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={project.image && project.image[0] ? project.image[0] : "https://assets.auxodev.dk/assets/4aae8b48-1e4d-42c2-9336-5940e0b091a6/th-byg-16-scaled.jpg"}
            alt={project.title}
            className="h-full w-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#11161a] via-[#11161a]/90 to-transparent" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-8">
          <div className="max-w-3xl">
            <Link to="/projekter" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#d92323] hover:text-white transition-colors mb-4">
              <i className="fa-solid fa-arrow-left" />
              <span>Tilbage til alle projekter</span>
            </Link>

            <span className="block text-xs font-extrabold uppercase tracking-widest text-amber-300">
              {project.service_type || "Tømrerarbejde"} {project.location ? `· ${project.location}` : ''}
            </span>

            <h1 className="text-3xl sm:text-5xl font-black text-white mt-2 tracking-tight leading-tight">
              {project.title}
            </h1>

            <p className="mt-4 text-slate-300 text-base sm:text-lg leading-relaxed">
              {project.summary}
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 sm:py-24 bg-white text-slate-900">
        <div className="mx-auto max-w-7xl px-4 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-8 space-y-8">
              <div 
                className="prose prose-slate max-w-none text-slate-800 leading-relaxed space-y-4 font-normal"
                dangerouslySetInnerHTML={{ __html: project.description }}
              />

              {/* Gallery */}
              {galleryList && galleryList.length > 0 && (
                <div className="pt-8 border-t border-slate-200">
                  <h3 className="text-2xl font-bold text-slate-900 mb-6">
                    Projektbilleder
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {galleryList.map((img, idx) => (
                      <div
                        key={idx}
                        className="group relative rounded-xl overflow-hidden bg-slate-100 border border-slate-200 shadow cursor-pointer h-64"
                        onClick={() => {
                          setActiveIndex(idx);
                          setLightboxOpen(true);
                        }}
                      >
                        <img
                          src={img}
                          alt={`${project.title} billede ${idx + 1}`}
                          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <i className="fa-solid fa-magnifying-glass-plus text-white text-2xl" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-4 space-y-6">
              <div className="bg-[#11161a] text-white p-6 sm:p-8 rounded-2xl shadow-xl sticky top-28 space-y-6">
                <h3 className="text-xl font-bold text-white border-b border-slate-800 pb-3">
                  Ønsker du en lignende løsning?
                </h3>
                <p className="text-sm text-slate-300">
                  Kontakt T.H Byg for et uforpligtende tilbud på dit projekt.
                </p>

                <div className="space-y-3 text-sm">
                  <a href="tel:28594349" className="flex items-center gap-3 p-3 bg-slate-800 rounded-full hover:bg-[#d92323] transition-colors whitespace-nowrap">
                    <i className="fa-solid fa-phone text-[#d92323]" />
                    <span className="font-bold text-white whitespace-nowrap">(+45) 28 59 43 49</span>
                  </a>
                  <a href="mailto:jacob.t.h.byg@gmail.com" className="flex items-center gap-3 p-3 bg-slate-800 rounded-full hover:bg-[#d92323] transition-colors whitespace-nowrap">
                    <i className="fa-solid fa-envelope text-[#d92323]" />
                    <span className="text-xs text-white truncate whitespace-nowrap">jacob.t.h.byg@gmail.com</span>
                  </a>
                </div>

                <Link
                  to="/kontakt"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#d92323] py-3.5 text-sm font-bold text-white hover:bg-[#b81b1b] transition-all"
                >
                  Indhent tilbud nu
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      <LightboxModal
        images={galleryList}
        currentIndex={activeImageIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        onNavigate={(idx) => setActiveIndex(idx)}
      />
    </div>
  );
}
